import { Component, Input, OnInit, ViewChild } from '@angular/core';
import { Account } from '@models/account/account.model';
import { FeatureFlag } from '@app/enums';
import { AuthService, NotifyService } from '@app/services';
import { FeatureService } from '@app/services/feature.service';
import { ConfirmationDialogComponent } from '@app/components';
import { PlatformModule } from '@app/platform.module';
import { TranslateModule } from '@ngx-translate/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';

@Component({
    selector: 'app-multi-factor-authentication',
    templateUrl: './multi-factor-authentication.template.html',
    standalone: true,
    imports: [PlatformModule, TranslateModule, CommonModule, FormsModule],
})
export class MultiFactorAuthentication implements OnInit {
    @ViewChild('confirmMfaChoice') confirmMfaChoiceModal!: ConfirmationDialogComponent;
    @Input() account: Account;

    isMFAAccountEnabled = false;
    isLoading = false;
    shouldShowConfirmationDialog = false;

    constructor(
        private readonly auth: AuthService,
        private readonly featureService: FeatureService,
        private readonly notify: NotifyService
    ) {}

    async ngOnInit(): Promise<void> {
        if (this.auth.isImpersonating) {
            return;
        }

        this.isMFAAccountEnabled = await this.featureService.has(FeatureFlag.MFAAccountEnabled);
        this.shouldShowConfirmationDialog = !this.account.isMFAEnabled;
    }

    async handleToggle(): Promise<void> {
        if (this.shouldShowConfirmationDialog) {
            const choiceConfirmed = await this.confirmMfaChoiceModal.open();

            if (!choiceConfirmed) {
                this.account.isMFAEnabled = false;
                return;
            }
        }

        this.isLoading = true;

        try {
            await this.account.save();
        } catch (error) {
            this.notify.error('account.accountUpdatedError');
            this.isLoading = false;
            return;
        }

        this.notify.success('account.accountUpdated');

        if (this.account.isMFAEnabled) {
            await this.auth.logout();
            return;
        }

        // If we got here, it means they've disabled MFA. We don't need a logout, but next time they
        // go to turn it back on, they should see the confirmation dialog.
        this.shouldShowConfirmationDialog = true;
        this.isLoading = false;
    }

    get isMFAEnabled(): boolean {
        if (this.account.isRoleBasedMfaEnabled) {
            return true;
        }
        return this.account.isMFAEnabled;
    }

    set isMFAEnabled(isMFAEnabled: boolean) {
        this.account.isMFAEnabled = isMFAEnabled;
    }
}
